import type {
  AdminCurrentRuntimeProcess,
  AdminRuntimeLocalService,
  RuntimeDiagnosticsResponse,
  RuntimeDiagnosticsTool,
} from "@cmmd-forge/contracts";

import type { EvidenceTab, StatusTone } from "./DiagnosticsSettings.types";

export interface LocalStatusCheck {
  readonly check: string;
  readonly state: string;
  readonly tone: StatusTone;
  readonly evidence: string;
  readonly jump: EvidenceTab;
}

export function summarizeRuntimeTools(tools: ReadonlyArray<RuntimeDiagnosticsTool>): string {
  if (tools.length === 0) return "No tools reported";
  const ok = tools.filter((tool) => tool.status === "ok").length;
  return `${ok} ok · ${tools.length - ok} error`;
}

function isCmmdNativeRuntimeDiagnostics(
  diagnostics: Pick<RuntimeDiagnosticsResponse, "runtime"> | undefined,
): boolean {
  return diagnostics?.runtime.cmmdRuntimeMode === true;
}

const CMMD_NATIVE_RUNTIME_SERVICE_NAMES = new Set([
  "Forge API",
  "WebSocket",
  "CMMD",
  "Provider grants",
]);

export function filterRuntimeInventoryServices(
  services: ReadonlyArray<AdminRuntimeLocalService>,
  diagnostics: Pick<RuntimeDiagnosticsResponse, "runtime"> | undefined,
): ReadonlyArray<AdminRuntimeLocalService> {
  if (!isCmmdNativeRuntimeDiagnostics(diagnostics)) return services;
  return services.filter((service) => CMMD_NATIVE_RUNTIME_SERVICE_NAMES.has(service.name));
}

export function shouldShowProviderEgressAttention(input: {
  readonly diagnostics: Pick<RuntimeDiagnosticsResponse, "runtime"> | undefined;
  readonly providerEgressReady: boolean;
}): boolean {
  return !input.providerEgressReady && !isCmmdNativeRuntimeDiagnostics(input.diagnostics);
}

export function shouldShowCmmdProviderGrantAttention(
  diagnostics: Pick<RuntimeDiagnosticsResponse, "runtime" | "localServices"> | undefined,
): boolean {
  return (
    isCmmdNativeRuntimeDiagnostics(diagnostics) &&
    diagnostics?.localServices?.some(
      (service) => service.name === "Provider grants" && service.status === "missing",
    ) === true
  );
}

export function formatCurrentProcessEvidence(
  process: AdminCurrentRuntimeProcess | undefined,
): string {
  if (!process) return "No local Forge process reported";
  const port = process.forgePort ? ` · port ${process.forgePort}` : "";
  const cwd = process.cwd ? ` · ${process.cwd}` : "";
  return `pid ${process.pid}${port}${cwd}`;
}

export function serviceEvidence(
  services: ReadonlyArray<AdminRuntimeLocalService>,
  serviceName: string,
  fallback: string,
): string {
  const service = services.find((item) => item.name === serviceName);
  if (!service) return fallback;
  return service.url ?? service.port ?? service.detail ?? fallback;
}
